import { BellRing, MoreHorizontal, Pencil, Plus, Timer, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { LineDialog } from "@/features/campaign-line-form";
import { StaffingAlertDialog, StaffingAlertSummary } from "@/features/line-staffing-alert";
import { ApiError } from "@/lib/api";
import { LANGUAGE_ROUTING, formatSpan, languageRoutingText } from "@/lib/campaign";
import { linePurposeText, ringStrategyText, skillStrategyText } from "@/lib/line";
import { formatRequirements, lineSkillsText } from "@/lib/skills";
import { describeStageRule } from "@/lib/stage-rules";
import { useDeleteLine, useUpdateCampaign } from "@/lib/queries";
import type { Campaign, Queue } from "@/lib/types";

/**
 * Where the campaign's calls wait for a rep, and how long each step may take (ADR-014).
 *
 * A campaign's lines are its own. The primary one comes and goes with the campaign; a second is
 * added only when some calls need different settings — the closers a setter hands off to, or an
 * inbound number with its own ring time. Every line, the primary included, is edited through
 * `PUT /queues/{id}`; the campaign's `routing` field writes the same row, so there is one editor
 * for both rather than two that can disagree.
 */
const TIMING_HEAD = "w-[1%] min-w-20 whitespace-normal py-2 leading-tight";

export function CampaignLines({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const remove = useDeleteLine();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Queue | undefined>();
  const [deleting, setDeleting] = React.useState<Queue | null>(null);
  const [alerting, setAlerting] = React.useState<Queue | null>(null);

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader
          title="Lines"
          description="Where the campaign's calls wait for a rep, and how long each step may take."
          action={
            canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(undefined);
                  setDialogOpen(true);
                }}
              >
                <Plus />
                Add a line
              </Button>
            )
          }
        />
        {campaign.lines.length === 0 ? (
          <CardBody>
            <Callout tone="warning">This campaign has no line, so its calls have nowhere to wait.</Callout>
          </CardBody>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line</TableHead>
                <TableHead>Rings</TableHead>
                <TableHead>Skills</TableHead>
                {/* The timing headings may wrap: the form uses the same words, and the values are short. */}
                <TableHead className={TIMING_HEAD}>Rings a rep for</TableHead>
                <TableHead className={TIMING_HEAD}>Wrap-up</TableHead>
                <TableHead className={TIMING_HEAD}>Disposition window</TableHead>
                <TableHead className={`${TIMING_HEAD} min-w-24`}>Caller waits up to</TableHead>
                <TableHead>Reps</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaign.lines.map((line) => (
                <TableRow key={line.id} className="[&>td]:h-auto [&>td]:py-3 [&>td]:align-top">
                  <TableCell className="max-w-[15rem]">
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <span className="font-mono text-xs text-text">{line.name}</span>
                        <Badge tone={line.purpose === "primary" ? "accent" : "neutral"} size="sm">
                          {linePurposeText(line.purpose).label}
                        </Badge>
                        {!line.enabled && (
                          <Badge tone="warning" size="sm">
                            Off
                          </Badge>
                        )}
                      </span>
                      {line.description && (
                        <span className="truncate text-xs text-text-muted" title={line.description}>
                          {line.description}
                        </span>
                      )}
                      <StaffingAlertSummary line={line} />
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="flex flex-col gap-0.5">
                      <span>{ringStrategyText(line.ring_strategy).label}</span>
                      {line.ring_strategy !== "ring_all" && (
                        <span className="text-xs text-text-muted">
                          {skillStrategyText(line.skill_strategy).label}
                        </span>
                      )}
                      {line.previous_rep_first && (
                        <span className="text-xs text-text-muted">Previous rep first</span>
                      )}
                      {(line.stage_rules ?? []).length > 0 && (
                        <span
                          className="text-xs text-text-muted"
                          title={line.stage_rules.map((r) => describeStageRule(r)).join("\n")}
                        >
                          {line.stage_rules.length === 1
                            ? "1 stage rule"
                            : `${line.stage_rules.length} stage rules`}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <LineSkillsSummary line={line} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{formatSpan(line.ring_timeout_seconds)}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {line.wrap_up_seconds > 0 ? formatSpan(line.wrap_up_seconds) : "None"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {line.disposition_seconds > 0 ? formatSpan(line.disposition_seconds) : "None"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {line.max_wait_seconds > 0 ? formatSpan(line.max_wait_seconds) : "No limit"}
                  </TableCell>
                  <TableCell>
                    <Badge tone={line.member_count > 0 ? "neutral" : "warning"}>{line.member_count}</Badge>
                  </TableCell>
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${line.name}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(line);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil />
                            Edit settings
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setAlerting(line)}>
                            <BellRing />
                            Nobody Ready alert
                          </DropdownMenuItem>
                          {line.purpose !== "primary" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onSelect={() => setDeleting(line)}>
                                <Trash2 />
                                Delete line
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <LanguageRouting campaign={campaign} canManage={canManage} />

      <LineDialog open={dialogOpen} onOpenChange={setDialogOpen} campaign={campaign} line={editing} />

      <StaffingAlertDialog
        line={alerting}
        open={alerting !== null}
        onOpenChange={(open) => !open && setAlerting(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this line?"
        description={
          <>
            <span className="font-mono">{deleting?.name}</span> and its staffing will be removed.
            Numbers routed to it, and reps signed in to it, block this.
          </>
        }
        confirmLabel="Delete line"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Line deleted", deleting.name);
            setDeleting(null);
          } catch (e) {
            toast.error("Could not delete the line", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </div>
  );
}

/**
 * What a line asks of its reps, in the table: each required skill, and when the requirement lifts
 * (`skill_relax_seconds`, ACL-273). A line with skills and no relax time says so, since its callers
 * wait for those reps however long it takes.
 */
function LineSkillsSummary({ line }: { line: Campaign["lines"][number] }) {
  const required = line.required_skills ?? [];
  const text = lineSkillsText(line);
  return (
    <span className="flex flex-col gap-1">
      {required.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {formatRequirements(required).split(", ").map((skill) => (
            <Badge key={skill} tone="outline" size="sm" className="font-mono">
              {skill}
            </Badge>
          ))}
        </span>
      ) : (
        <span className="whitespace-nowrap text-text-muted">{text.skills}</span>
      )}
      {text.relax ? (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-text-muted">
          <Timer className="size-3 shrink-0" aria-hidden />
          {text.relax}
        </span>
      ) : (
        required.length > 0 && (
          <span className="whitespace-nowrap text-xs text-text-subtle">Only these reps</span>
        )
      )}
    </span>
  );
}

/** A campaign setting, saved as soon as it is picked: one control, nothing to batch it with. */
function LanguageRouting({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();

  return (
    <Card>
      <CardHeader
        title="Language"
        description="Whether a lead's language decides which rep takes their call. A rep's languages are their skills."
      />
      <CardBody>
        <Field
          label="Language routing"
          htmlFor="campaign-language-routing"
          hint={languageRoutingText(campaign.language_routing).description}
        >
          <Select
            value={campaign.language_routing}
            disabled={!canManage || update.isPending}
            onValueChange={(next) =>
              update.mutate(
                { id: campaign.id, language_routing: next },
                {
                  onSuccess: () => toast.success("Language routing saved", languageRoutingText(next).label),
                  onError: (e) =>
                    toast.error("Could not save", e instanceof ApiError ? e.message : "Please try again."),
                },
              )
            }
          >
            <SelectTrigger id="campaign-language-routing" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_ROUTING.map((value) => (
                <SelectItem key={value} value={value} description={languageRoutingText(value).description}>
                  {languageRoutingText(value).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </CardBody>
    </Card>
  );
}
